/**
 * Service layer — Studio Calendar (Staff PTO + Studio Holidays).
 *
 * Owns the studio_calendar_events table (Supabase) behind the Clerk-authed /api/studio-calendar
 * endpoint. Components never call the API directly — they go through here.
 *
 * Event shape: { id, event_type: 'pto'|'holiday', member_airtable_id, member_name, holiday_name,
 * start_date, end_date, created_at }.
 */

/**
 * Get a Clerk token, waiting briefly for the getter to exist — the workspace can mount and fetch
 * on a refresh-restore before OutlineView installs window.__clerkGetToken. Mirrors
 * services/tracker.js / services/notifications.js.
 */
async function token() {
  for (let i = 0; i < 25; i++) {
    if (typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function') {
      try { const t = await window.__clerkGetToken(); if (t) return t } catch { /* retry */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}

async function post(payload) {
  const headers = { 'Content-Type': 'application/json' }
  const t = await token(); if (t) headers['Authorization'] = 'Bearer ' + t
  const res = await fetch('/api/studio-calendar', { method: 'POST', headers, body: JSON.stringify(payload) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/studio-calendar' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}

export async function listStudioCalendarEvents({ from, to } = {}) {
  return (await post({ action: 'list', from, to })).events || []
}

export async function createPto({ memberAirtableId, memberName, startDate, endDate, kind }) {
  return (await post({ action: 'createPto', memberAirtableId, memberName, startDate, endDate, kind })).event
}

/** Same as createPto, also returning the draft time entries it made: { event, timeCard: { created:[dates], skipped:[dates], forSomeoneElse? } }. */
export async function createPtoWithTimeCard({ memberAirtableId, memberName, startDate, endDate, kind }) {
  return post({ action: 'createPto', memberAirtableId, memberName, startDate, endDate, kind })
}
export async function updatePto(id, fields) { return (await post({ action: 'updatePto', id, ...fields })).event }
export async function deletePto(id) { return (await post({ action: 'deletePto', id })).ok }

export async function createHoliday({ holidayName, startDate, endDate }) {
  return (await post({ action: 'createHoliday', holidayName, startDate, endDate })).event
}
export async function updateHoliday(id, fields) { return (await post({ action: 'updateHoliday', id, ...fields })).event }
export async function deleteHoliday(id) { return (await post({ action: 'deleteHoliday', id })).ok }
